import { createFileRoute, redirect } from "@tanstack/react-router";

import { H2 } from "@/components/ui/typography";
import {
  clearLoginSuccessType,
  getLoginSuccessType,
  loginOAuth,
  LoginSuccessType,
} from "@/lib/api";

export const Route = createFileRoute("/app/auth/callback")({
  component: SuccessRoute,
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    redirect?: string;
    code?: string;
  } => {
    // validate and parse the search params into a typed state
    return {
      redirect: search?.redirect as string | undefined,
      code: search?.code as string | undefined,
    };
  },
  beforeLoad: async ({ search }) => {
    if (!search.code) {
      throw redirect({ to: "/login" });
    }

    const res = await loginOAuth(search.code);

    // Get success type after login (cookie is cleared after reading)
    const successType = getLoginSuccessType();
    clearLoginSuccessType();

    // For ATProtoFans flow, redirect to Stripe connection page after login
    if (successType === LoginSuccessType.AT_PROTOFANS_STRIPE) {
      throw redirect({ to: "/atprotofans/connect-stripe" });
    }

    // The co/core elevation round trip: this sign-in was asked for solely to
    // pick up the co/core scope, so land back where it started and let the
    // account page finish the connection it was in the middle of.
    if (successType === LoginSuccessType.COCORE_CONNECT) {
      throw redirect({ to: "/app/account", search: { cocore: "elevated" } });
    }

    // The user logged in normally (not a Patreon linking flow)
    if (!res || !("linked" in res)) {
      throw redirect({ to: "/app" });
    }

    if (res.linked === false) {
      throw new Error(
        "Failed to link Patreon and Bluesky. Please contact support.",
      );
    }
  },
  head: () => ({ meta: [{ title: "Auth" }] }),
});

function SuccessRoute() {
  return (
    <div className="flex items-center justify-center h-screen">
      <div className="flex flex-col gap-2">
        <H2>Patreon and Bluesky linked!</H2>
        <p>For feeds you subscribe to, you can now receive member benefits.</p>
      </div>
    </div>
  );
}
